import { useConfirmation } from "shared/components/ConfirmationProvider";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  InputAdornment,
  Typography,
  Chip,
  Divider,
  alpha,
  LinearProgress,
} from "@mui/material";
import {
  AddBox,
  Save,
  Close,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Router,
  Dns,
  Settings,
  Memory,
  CloudQueue,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import PropTypes from "prop-types";

const SwitchCreateDialog = ({ open, onClose, onCreate, softwareOptions = [], loading = false }) => {
  const confirm = useConfirmation();

  const [formData, setFormData] = useState({
    nameSwitch: "",
    ipSwitch: "",
    software: "",
    hardware: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [responseData, setResponseData] = useState(null);

  // Resetear formulario cuando se abre/cierra
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setFormData({
      nameSwitch: "",
      ipSwitch: "",
      software: "",
      hardware: "",
    });
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
    setSubmitStatus(null);
    setStatusMessage("");
    setResponseData(null);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    validateField(field, value);
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "nameSwitch":
        if (!value) error = "El nombre del switch es requerido";
        else if (value.length < 3) error = "El nombre debe tener al menos 3 caracteres";
        else if (value.length > 50) error = "El nombre no puede exceder 50 caracteres";
        break;
      case "ipSwitch":
        if (!value) error = "La dirección IP es requerida";
        else if (!isValidIP(value))
          error = "Ingrese una dirección IP válida (formato: xxx.xxx.xxx.xxx)";
        break;
      case "software":
        if (!value) error = "Debe seleccionar una versión de software";
        break;
      case "hardware":
        if (!value) error = "El hardware es requerido";
        else if (!/^\d+$/.test(value)) error = "El hardware debe ser numérico";
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  // Validar formato de IP
  const isValidIP = (ip) => {
    const ipRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const validateForm = () => {
    const fieldsToValidate = ["nameSwitch", "ipSwitch", "software", "hardware"];
    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) isValid = false;
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    });

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitStatus("error");
      setStatusMessage("Por favor, complete todos los campos requeridos correctamente.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage("Creando switch...");
    setResponseData(null);

    try {
      const result = await onCreate(formData);

      if (result && result.success) {
        setResponseData(result.data);
        setStatusMessage(result.message || "Switch creado exitosamente.");
        setSubmitStatus("success");

        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(result?.error || "Error al crear el switch");
      }
    } catch (error) {
      console.error("Error al crear switch:", error);
      setSubmitStatus("error");
      setStatusMessage(error.message || "Error desconocido al crear el switch.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (isSubmitting) {
      if (await confirm("La creación del switch está en progreso. ¿Desea cancelar?")) {
        setIsSubmitting(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const renderForm = () => (
    <Stack spacing={3}>
      {/* Formulario principal */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Grid container spacing={3}>
          {/* Nombre Switch */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nombre del Switch *"
              value={formData.nameSwitch}
              onChange={handleChange("nameSwitch")}
              error={isFieldError("nameSwitch")}
              helperText={isFieldError("nameSwitch") && errors.nameSwitch}
              disabled={isSubmitting}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Router sx={{ color: "#5c6bc0" }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Ej: Switch Principal Piso 1"
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
              }}
            />
          </Grid>

          {/* IP Switch */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Dirección IP *"
              value={formData.ipSwitch}
              onChange={handleChange("ipSwitch")}
              error={isFieldError("ipSwitch")}
              helperText={isFieldError("ipSwitch") && errors.ipSwitch}
              disabled={isSubmitting}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Dns sx={{ color: "#5c6bc0" }} />
                  </InputAdornment>
                ),
              }}
              placeholder="192.168.1.100"
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
              }}
            />
          </Grid>

          {/* Software */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("software")}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
              }}
            >
              <InputLabel>Versión Software *</InputLabel>
              <Select
                value={formData.software}
                onChange={handleChange("software")}
                label="Versión Software *"
                disabled={isSubmitting || loading}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <CloudQueue sx={{ color: "#5c6bc0" }} />
                  </InputAdornment>
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: "0.95rem", fontWeight: 500 }}>
                      {selected}
                    </Typography>
                  </Box>
                )}
              >
                {loading ? (
                  <MenuItem disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <Typography>Cargando opciones...</Typography>
                    </Box>
                  </MenuItem>
                ) : softwareOptions.length > 0 ? (
                  softwareOptions.map((software) => (
                    <MenuItem key={software} value={software}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <CloudQueue sx={{ color: "#5c6bc0" }} />
                        <Typography variant="body1" sx={{ fontSize: "0.95rem" }}>
                          {software}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography color="text.secondary">No hay opciones disponibles</Typography>
                  </MenuItem>
                )}
              </Select>
              {isFieldError("software") && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.software}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Hardware */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hardware *"
              value={formData.hardware}
              onChange={handleChange("hardware")}
              error={isFieldError("hardware")}
              helperText={isFieldError("hardware") && errors.hardware}
              disabled={isSubmitting}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Memory sx={{ color: "#5c6bc0" }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Ej: 6"
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* RESUMEN DEL SWITCH */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: alpha("#5c6bc0", 0.3),
          backgroundColor: alpha("#5c6bc0", 0.02),
        }}
      >
        <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
          RESUMEN DEL SWITCH
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              NOMBRE
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
              {formData.nameSwitch || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              DIRECCIÓN IP
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
              {formData.ipSwitch || "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              SOFTWARE
            </Typography>
            <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
              {formData.software || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              HARDWARE
            </Typography>
            <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.9rem" }}>
              {formData.hardware || "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Barra de progreso de completitud */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              COMPLETITUD DEL FORMULARIO
            </Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
              {Object.values(formData).filter((v) => v && v !== "").length}/4 campos
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Object.values(formData).filter((v) => v && v !== "").length * 25}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha("#5c6bc0", 0.1),
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#5c6bc0",
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {Object.values(formData).every((v) => v && v !== "") ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: alpha("#4caf50", 0.05),
              border: "1px dashed",
              borderColor: alpha("#4caf50", 0.3),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Todos los campos requeridos están completos. Puede proceder a crear el switch.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: alpha("#ff9800", 0.05),
              border: "1px dashed",
              borderColor: alpha("#ff9800", 0.3),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Warning sx={{ color: "#ff9800", fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Complete todos los campos requeridos para continuar
            </Typography>
          </Box>
        )}
      </Paper>
    </Stack>
  );

  const renderSuccessContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 3 }} />
      <Typography variant="h6" fontWeight="bold" color="success" gutterBottom>
        Switch Creado Exitosamente
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {statusMessage}
      </Typography>
      {responseData && (
        <Chip
          label={`ID: ${responseData?.switch?.idswitch || "N/A"}`}
          color="success"
          variant="outlined"
        />
      )}
    </Box>
  );

  const renderErrorContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 3 }} />
      <Typography variant="h6" fontWeight="bold" color="error" gutterBottom>
        Error al Crear Switch
      </Typography>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="body2">{statusMessage}</Typography>
      </Alert>
    </Box>
  );

  const renderLoadingContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CircularProgress size={80} thickness={4} sx={{ color: "primary.main", mb: 3 }} />
      <Typography variant="h6" fontWeight="medium" color="primary" gutterBottom>
        Creando Switch
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {statusMessage}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : (
            <AddBox sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Crear Nuevo Switch
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete todos los campos para crear un nuevo switch
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Stack spacing={3}>
          {(isSubmitting || submitStatus) && submitStatus !== "success" && (
            <Alert
              severity={isSubmitting ? "info" : submitStatus === "success" ? "success" : "error"}
              icon={isSubmitting ? <CircularProgress size={20} /> : undefined}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusMessage}
              </Typography>
            </Alert>
          )}

          {isSubmitting && renderLoadingContent()}
          {submitStatus === "success" && renderSuccessContent()}
          {submitStatus === "error" && renderErrorContent()}
          {!isSubmitting && !submitStatus && renderForm()}
        </Stack>
      </DialogContent>

      {/* Footer */}
      {!isSubmitting && !submitStatus && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{
                px: 3,
                minWidth: 120,
                borderRadius: "8px",
                height: "48px",
              }}
            >
              Cancelar
            </MDButton>
            <MDButton
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              startIcon={<Save />}
              disabled={isSubmitting || Object.values(formData).some((v) => !v)}
              sx={{
                px: 4,
                minWidth: 200,
                borderRadius: "8px",
                fontWeight: "bold",
                height: "48px",
              }}
            >
              CREAR SWITCH
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "error" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cerrar
            </MDButton>
            <MDButton
              onClick={() => {
                setSubmitStatus(null);
                setStatusMessage("");
              }}
              variant="contained"
              color="warning"
              startIcon={<Refresh />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Reintentar
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "success" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <MDButton
              onClick={handleClose}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
            >
              Aceptar
            </MDButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

SwitchCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  softwareOptions: PropTypes.array,
  loading: PropTypes.bool,
};

SwitchCreateDialog.defaultProps = {
  softwareOptions: [],
  loading: false,
};

export default React.memo(SwitchCreateDialog);
